import React, { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { ArrowRightIcon, XIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { tips, TipCard } from './ConsularAffairs';
export function ConsularBanner() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-40px'
  });
  const {
    t,
    language
  } = useLanguage();
  const [modalOpen, setModalOpen] = useState(false);
  const isEs = language === 'ES';
  return <section ref={ref} className="bg-haa-blue py-10 px-4" aria-label="Consular Affairs quick link" data-id="element-1259">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={inView ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1]
    }} className="flex flex-col items-center gap-4 text-center" data-id="element-1260">
        <p className="font-nunito text-white/80 text-sm uppercase tracking-[0.3em] font-semibold" data-id="element-1261">
          {t('consularBanner.label')}
        </p>
        <h2 className="font-bebas text-4xl sm:text-5xl text-white tracking-wider leading-none" data-id="element-1262">
          {t('consularBanner.heading')}
        </h2>
        <motion.button onClick={() => setModalOpen(true)} whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.97
      }} className="inline-flex items-center gap-2 bg-white text-haa-blue font-nunito font-bold text-sm px-8 py-3 rounded-full shadow-lg hover:bg-haa-skyblue hover:text-white transition-all duration-300 mt-2" data-id="element-1263">
          {t('consularBanner.button')}
          <ArrowRightIcon className="w-4 h-4" data-id="element-1264" />
        </motion.button>
      </motion.div>

      <AnimatePresence data-id="element-1265">
        {modalOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-black/60 backdrop-blur-sm" onClick={() => setModalOpen(false)} data-id="element-1266">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.3,
          ease: [0.16, 1, 0.3, 1]
        }} className="bg-gray-50 rounded-2xl shadow-2xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-1267">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white" data-id="element-1268">
                <div data-id="element-1269">
                  <h2 className="font-bebas text-2xl sm:text-3xl text-haa-navy tracking-wider" data-id="element-1270">
                    {t('consular.heading')}
                  </h2>
                  <p className="font-nunito text-gray-500 text-sm mt-1" data-id="element-1271">
                    {isEs ? 'Información y Servicios Consulares' : 'Consular Information & Services'}
                  </p>
                </div>
                <button onClick={() => setModalOpen(false)} className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600" aria-label="Close" data-id="element-1272">
                  <XIcon className="w-6 h-6" data-id="element-1273" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6" data-id="element-1274">
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6" data-id="element-1275">
                  {tips.map((tip, i) => <TipCard key={tip.titleKey + i} tip={tip} index={i} data-id="element-1276" />)}
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-200 bg-white flex items-center justify-between" data-id="element-1277">
                <a href="#consular-affairs" onClick={e => {
              e.preventDefault();
              setModalOpen(false);
              setTimeout(() => {
                const el = document.getElementById('consular-affairs');
                if (el) {
                  const navbarHeight = 88;
                  const pos = el.getBoundingClientRect().top + window.scrollY;
                  window.scrollTo({
                    top: pos - navbarHeight,
                    behavior: 'smooth'
                  });
                }
              }, 350);
            }} className="font-nunito text-haa-blue hover:text-haa-navy text-sm font-semibold hover:underline transition-colors inline-flex items-center gap-1" data-id="element-1278">
                  {isEs ? 'Ver Sección Completa ↓' : 'View Full Section ↓'}
                </a>
                <button onClick={() => setModalOpen(false)} className="px-6 py-2.5 bg-haa-navy hover:bg-haa-blue text-white font-nunito font-bold text-sm rounded-xl transition-colors" data-id="element-1279">
                  {isEs ? 'Cerrar' : 'Close'}
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}